import { createFileRoute, Link } from '@tanstack/react-router'
import { MoreHorizontalIcon } from 'lucide-react'

import { PageLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { KeyValueDisplay, type KeyValueItem } from '@/components/ui/data-display'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { Table } from '@/components/ui/table'

import { purchaseOrderApi } from '@/features/inventory/api/purchase-order-api'

import { dateId, toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/inventory/po/$id/')({
  loader: async ({ context: { qc }, params: { id } }) => {
    const { data } = await qc.ensureQueryData(purchaseOrderApi.detail.query({ params: { id } }))
    return {
      data,
      crumb: data.order.number,
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <PageLayout>
      <SectionOverview />
      <SectionTableItems />
      <SectionActivity />
    </PageLayout>
  )
}

function SectionOverview() {
  const { data } = Route.useLoaderData()
  const { order, outlet, sourceOutlet } = data

  const overview: KeyValueItem[] = [
    { key: 'Outlet', value: `${outlet.name} (${outlet.code})` },
    { key: 'From Supplier', value: `${sourceOutlet.name} (${sourceOutlet.code})` },
    { key: 'Date', value: dateId(order.date) },
    {
      key: 'Status',
      value: () => <Badge variant='secondary'>{order.status.toUpperCase()}</Badge>,
    },
    { key: 'Total', value: toIdr(order.total) },
  ]

  return (
    <Card>
      <Card.Header>
        <Card.Title>PO-12312321-21313</Card.Title>
        <Card.Description>Purchase Order</Card.Description>
        <Card.Action className=''>
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button size='icon' variant={'outline'}>
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align='end'>
              <DropdownMenu.Label>My Account</DropdownMenu.Label>
              <DropdownMenu.Separator />
              <DropdownMenu.Item asChild>
                <Link to='/inventory/category'>Profile</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item>Billing</DropdownMenu.Item>
              <DropdownMenu.Item>Team</DropdownMenu.Item>
              <DropdownMenu.Item>Subscription</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </Card.Action>
      </Card.Header>
      <Card.Content>
        <KeyValueDisplay items={overview} />
      </Card.Content>
    </Card>
  )
}

function SectionTableItems() {
  const { data } = Route.useLoaderData()
  const {
    order: { items },
  } = data

  return (
    <Card>
      <Card.Header>
        <Card.Title>Daftar Items</Card.Title>
      </Card.Header>
      <Card.Content className='px-4 py-2'>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Bahan Baku</Table.Head>
              <Table.Head>Jumlah</Table.Head>
              <Table.Head>Satuan</Table.Head>
              <Table.Head>Harga Satuan</Table.Head>
              <Table.Head>Total</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map(item => (
              <Table.Row key={item.id}>
                <Table.Cell>{item.name}</Table.Cell>
                <Table.Cell>{item.qty}</Table.Cell>
                <Table.Cell>{item.uom}</Table.Cell>
                <Table.Cell>
                  {toIdr(item.price)}/{item.uom}
                </Table.Cell>
                <Table.Cell>{toIdr(item.subtotal)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card.Content>
    </Card>
  )
}

function SectionActivity() {
  const {
    data: { order },
  } = Route.useLoaderData()

  const activity: KeyValueItem[] = [
    {
      key: 'Dibuat',
      value: dateId(order.createdAt),
    },
    {
      key: 'Diperbaharui',
      value: dateId(order.updatedAt),
    },
  ]

  return (
    <Card>
      <Card.Header>
        <Card.Title>Aktivitas</Card.Title>
      </Card.Header>
      <Card.Content>
        <KeyValueDisplay items={activity} />
      </Card.Content>
    </Card>
  )
}
